/**
 * Audio engine for the Chapter 1 landing.
 *
 * Three channels under a single AudioContext:
 *
 *   · music   — ambient backtrack, looped at ~30% volume under everything
 *   · voice   — per-beat TTS narration, routed through a procedural
 *               convolution reverb for the "space/angelic" halo
 *   · sfx     — one-shot cinematic hit points at key beats
 *
 * Buffers are fetched lazily the first time each asset is needed, then
 * cached. Reverb impulse response is synthesized on init — no IR file
 * required. Autoplay blockers are respected: nothing plays until
 * `start()` is called from a user gesture (the tap-to-start overlay).
 */

interface VoiceTrigger {
  /** 0-based beat index into the BEATS array. */
  beatIndex: number;
}

interface SfxTrigger {
  name: string;
  /** Per-call gain multiplier in [0, 1]. Subtle atmospheric SFX use
   *  low values (0.3-0.45); big hits like the finale use 1.0. */
  gain?: number;
}

/** A single wave-hum source derived from a scene emitter. Driven from
 *  the render loop each frame — the audio engine smooths frequency and
 *  gain transitions internally so callers can just stream targets. */
export interface HumSource {
  /** Stable identifier so voices persist across frames (e.g. `"e3"`). */
  id: string;
  /** Target audio frequency in Hz. The Chapter1 engine maps each
   *  emitter's visual wave rate to audio Hz before calling. */
  freq: number;
  /** 0..1 target gain for this voice. Caller-scaled by emit strength. */
  gain: number;
}

export interface AudioEngineHandle {
  /** True once start() has run successfully (user gesture captured). */
  ready(): boolean;
  /** Kick off the engine on a user gesture — resumes AudioContext,
   *  starts the music bed, and preloads frequently-used buffers. */
  start(): Promise<void>;
  /** Play a voice clip for a beat index. No-ops if not started. */
  playVoice(t: VoiceTrigger): void;
  /** Play a named SFX clip. No-ops if not started. */
  playSfx(t: SfxTrigger): void;
  /** Ramp the music bed to `gain` (linear) over `durationSec`. Used to
   *  fade the bed down before the finale so it doesn't loop mid-reveal. */
  fadeMusicTo(gain: number, durationSec: number): void;
  /** Ramp the voice channel to `multiplier × default` over `durationSec`.
   *  1.0 = full voice, 0 = silent. Affects dry and wet buses together
   *  so the reverb duck tracks the direct path exactly. */
  fadeVoiceTo(multiplier: number, durationSec: number): void;
  /** Stream the current set of active wave-emitters each frame. The
   *  engine manages per-source oscillator voices internally: creates
   *  voices for new ids, smoothly ramps frequency and gain for
   *  existing ones, and fades+releases voices whose ids drop off. */
  setHumSources(sources: ReadonlyArray<HumSource>): void;
  /** Fire a brief tonal pluck — a short sine-wave note that swells
   *  and decays. Used for "dopamine spikes" on beat transitions in
   *  scenes without wave emitters (where the hum synth is silent).
   *  Velocity 0..1 scales loudness; freqHz is the pitch. */
  triggerPluck(opts: { freqHz: number; velocity?: number; decaySec?: number }): void;
  /** Global mute — applies to all channels. */
  setMuted(muted: boolean): void;
  muted(): boolean;
  dispose(): void;
}

const VOICE_DIR = "/audio/voice";
const SFX_DIR = "/audio/sfx";

// Per-session cache-buster. Each page load gets a new value so the
// browser HTTP cache + any in-memory buffer cache can't serve stale
// voice/SFX clips after a dev-server rebuild. Within one session the
// URL is stable so buffers cache normally.
const CACHE_BUST = `?v=${Date.now().toString(36)}`;
const MUSIC_PATH = "/audio/music/backtrack.mp3";

// Music starts silent. MUSIC_CUES in Chapter1.tsx drives the whole
// dynamic arc — the first beat is pure voice (no bed under "for the
// first time in human history,"), then music swells in on beat 1.
const MUSIC_GAIN = 0.0;
const VOICE_DRY_GAIN = 0.76;
// Reverb bus was bleeding into the next word at this tempo — tail of
// short beats like "sadness" ghosts into "ness" and reads as a stutter.
// Keep just enough room tone to feel cinematic, not enough to echo.
const VOICE_WET_GAIN = 0.28;
// SFX channel is effectively dedicated to the pulsar finale hit —
// push it louder so the rumble lands with weight under the reveal.
const SFX_GAIN = 0.95;

// ── Wave-hum synth constants ─────────────────────────────────────────
// Tonal bed running CONTINUOUSLY throughout the whole film — every
// scene with emitters plays its own quiet binaural pair. The hum
// never climbs in volume, never pulls focus; its job is to provide
// a constant subliminal dopamine texture that colors the emotional
// arc without the viewer ever noticing it as "the music."
//
// Design principle (binaural beats, not chorus):
//   - oscA plays the fundamental (visual emitFreq → 90·2^(f/7) Hz,
//     sits in the 100–220 Hz body-felt range).
//   - oscB plays fundamental + BINAURAL_BEAT_HZ as an absolute Hz
//     offset, NOT cents. At any fundamental, the perceived amplitude-
//     modulation beat frequency stays constant — that's the real
//     binaural beat effect (brain extracts the difference frequency
//     as a sub-audible rhythm).
//   - 7 Hz beat = lower alpha / upper theta boundary. Warm, relaxed-
//     alert, pleasurable. Not screechy, not sleepy, not distracting.
const HUMS_BUS_GAIN = 0.11;
const HUM_VOICE_PEAK = 0.16;
const HUM_RAMP_SEC = 0.25;
const HUM_RELEASE_SEC = 0.7;
// Warm low-mid lowpass. 600 Hz lets fundamentals (100-220 Hz bass
// drone range) through cleanly while rolling off anything above
// that could turn shrill.
const HUMS_FILTER_HZ = 600;
const HUMS_FILTER_Q = 0.15;
// Binaural beat in absolute Hz. Must stay constant across voice
// freq changes — brainwave entrainment depends on the beat freq
// not wandering. 7 Hz sits between theta (emotional/dreamlike) and
// alpha (relaxed/receptive) — the sweet spot for a film that wants
// to keep the viewer engaged AND feeling.
const BINAURAL_BEAT_HZ = 7;
// Hard-cap at 2 simultaneous voices. Even 3 similar bass tones can
// beat into muddy interference — 2 is the maximum for a clean warm
// drone, and it still tells the story (e.g., the pair physics will
// have both orbs as voices).
const HUM_MAX_VOICES = 2;

interface HumVoice {
  /** Fundamental — the tonal anchor. */
  oscA: OscillatorNode;
  /** Detuned partner for subtle chorus warmth. */
  oscB: OscillatorNode;
  bGain: GainNode;
  voiceGain: GainNode;
  lastSeen: number;
  curFreq: number;
  curGain: number;
}

export function createAudioEngine(): AudioEngineHandle {
  let ctx: AudioContext | null = null;
  let musicBus: GainNode | null = null;
  let voiceDryBus: GainNode | null = null;
  let voiceWetBus: GainNode | null = null;
  let sfxBus: GainNode | null = null;
  let masterBus: GainNode | null = null;
  let reverbConvolver: ConvolverNode | null = null;
  let musicSource: AudioBufferSourceNode | null = null;
  // Currently-playing voice clip. Each new beat kills this one with a
  // short fade before starting its own — prevents pile-up when the
  // viewer spams next/prev/restart.
  let voiceSource: AudioBufferSourceNode | null = null;
  let voiceGate: GainNode | null = null;
  // Monotonic counter so async buffer loads from a stale beat can't
  // "win the race" and start playing after a newer voice has begun.
  let voiceToken = 0;
  let started = false;
  let muted = false;

  const buffers = new Map<string, AudioBuffer>();
  const inFlight = new Map<string, Promise<AudioBuffer | null>>();

  // Hum synth — lazily spun up on first call so we don't pay the node
  // allocation for beats that never emit.
  let humsBus: GainNode | null = null;
  let humsFilter: BiquadFilterNode | null = null;
  const humVoices = new Map<string, HumVoice>();
  let humTurn = 0;

  async function loadBuffer(url: string): Promise<AudioBuffer | null> {
    if (!ctx) return null;
    const cached = buffers.get(url);
    if (cached) return cached;
    const pending = inFlight.get(url);
    if (pending) return pending;

    const p = (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const data = await res.arrayBuffer();
        const buf = await ctx!.decodeAudioData(data);
        buffers.set(url, buf);
        return buf;
      } catch {
        return null;
      } finally {
        inFlight.delete(url);
      }
    })();
    inFlight.set(url, p);
    return p;
  }

  /**
   * Build a "cathedral space" impulse response procedurally. Exponentially
   * decaying stereo noise, pre-delay, soft tonal shaping via two filter
   * taps. No external IR file, no extra bundle weight.
   */
  function buildReverbIR(): AudioBuffer {
    const sampleRate = ctx!.sampleRate;
    // Cathedral-lite — 2.0s decay. Long enough that you FEEL the
    // room around Tess (the cinematic "space" the user wants back)
    // but short enough that with the trimmed clips + beat breathing
    // room, the tail decays to silence before the next line opens.
    const duration = 2.0;
    const frames = Math.floor(sampleRate * duration);
    const ir = ctx!.createBuffer(2, frames, sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const data = ir.getChannelData(ch);
      // 20ms pre-delay so the dry voice lands first, then the wet tail
      // opens — that's what gives it the "room around it" quality
      // rather than a muddy overlap.
      const preDelay = Math.floor(sampleRate * 0.02);
      for (let i = 0; i < frames; i++) {
        if (i < preDelay) {
          data[i] = 0;
          continue;
        }
        const t = (i - preDelay) / (frames - preDelay);
        // Two-stage decay, tuned faster for the shorter length so the
        // tail actually reaches silence by the end of the buffer.
        const early = Math.exp(-t * 9) * 0.55;
        const tail = Math.exp(-t * 4.2) * 0.38;
        // Slight channel decorrelation for stereo width.
        const phaseOffset = ch === 0 ? 0 : 0.0007;
        const noise =
          Math.random() * 2 - 1 + Math.sin(i * 0.02 + phaseOffset) * 0.05;
        data[i] = noise * (early + tail);
      }
    }
    return ir;
  }

  async function start(): Promise<void> {
    if (started) return;
    const AC: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;

    // ── Synchronous, gesture-scoped setup ───────────────────────────
    // Everything from here until the `await ctx.resume()` below must
    // happen in the same JavaScript task as the user gesture —
    // otherwise iOS Safari keeps the AudioContext locked and no sound
    // ever plays, even though the context reports running.
    ctx = new AC();

    // iOS unlock ritual — schedule a zero-length silent buffer on the
    // destination so WebKit marks the AudioContext as "unlocked" before
    // any async boundary. Without this, the first real play() is
    // silenced on iPhone Safari.
    const silentBuf = ctx.createBuffer(1, 1, 22050);
    const silentSrc = ctx.createBufferSource();
    silentSrc.buffer = silentBuf;
    silentSrc.connect(ctx.destination);
    silentSrc.start(0);

    masterBus = ctx.createGain();
    masterBus.gain.value = 1;
    masterBus.connect(ctx.destination);

    musicBus = ctx.createGain();
    musicBus.gain.value = MUSIC_GAIN;
    musicBus.connect(masterBus);

    sfxBus = ctx.createGain();
    sfxBus.gain.value = SFX_GAIN;
    sfxBus.connect(masterBus);

    // Voice routes through dry + wet. Wet goes through a convolver for
    // the space reverb. Dry keeps the voice forward and intelligible.
    voiceDryBus = ctx.createGain();
    voiceDryBus.gain.value = VOICE_DRY_GAIN;
    voiceDryBus.connect(masterBus);

    voiceWetBus = ctx.createGain();
    voiceWetBus.gain.value = VOICE_WET_GAIN;
    reverbConvolver = ctx.createConvolver();
    reverbConvolver.buffer = buildReverbIR();
    voiceWetBus.connect(reverbConvolver);
    reverbConvolver.connect(masterBus);

    // Flag as started synchronously so any beat-change effect racing
    // this function can already queue plays (they'll be held in
    // loadBuffer() and play once the first buffer resolves).
    started = true;

    // ── Async tail — safe now that the context is unlocked ─────────
    if (ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch {
        // Older iOS: resume() may reject if called before the gesture
        // finishes bubbling; the silent buffer above still unlocks us.
      }
    }
    void startMusicBed();
  }

  async function startMusicBed(): Promise<void> {
    if (!ctx || !musicBus) return;
    const buf = await loadBuffer(MUSIC_PATH + CACHE_BUST);
    if (!buf) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.connect(musicBus);
    src.start();
    musicSource = src;
  }

  /**
   * Fade out the currently-playing voice clip (short ramp to avoid a
   * click), then stop it. Safe to call even if nothing is playing.
   */
  function stopCurrentVoice(fadeSec = 0.08): void {
    if (!ctx) return;
    const gate = voiceGate;
    const src = voiceSource;
    if (!gate || !src) return;
    voiceGate = null;
    voiceSource = null;
    const now = ctx.currentTime;
    try {
      gate.gain.cancelScheduledValues(now);
      gate.gain.setValueAtTime(gate.gain.value, now);
      gate.gain.linearRampToValueAtTime(0, now + fadeSec);
      src.stop(now + fadeSec + 0.01);
    } catch {
      // already stopped or disconnected — ignore
    }
  }

  function playVoice(t: VoiceTrigger): void {
    if (!ctx || !voiceDryBus || !voiceWetBus || !started) return;
    const idx = t.beatIndex.toString().padStart(2, "0");
    const url = `${VOICE_DIR}/beat-${idx}.mp3${CACHE_BUST}`;

    // Kill whatever is playing right now and claim a fresh token. The
    // async loadBuffer below might resolve after the user has already
    // skipped past this beat — the token check prevents that stale
    // clip from sneaking in on top of a newer one.
    stopCurrentVoice();
    const myToken = ++voiceToken;

    void loadBuffer(url).then((buf) => {
      if (!buf || !ctx || !voiceDryBus || !voiceWetBus) return;
      if (myToken !== voiceToken) return; // a newer voice has taken over

      // Onset ramp — 25ms fade-in prevents decoder clicks and makes the
      // clip's first consonant feel intentional rather than snapped on.
      const startAt = ctx.currentTime;
      const onset = 0.025;
      const gate = ctx.createGain();
      gate.gain.setValueAtTime(0, startAt);
      gate.gain.linearRampToValueAtTime(1, startAt + onset);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(gate);
      gate.connect(voiceDryBus);
      gate.connect(voiceWetBus);

      src.onended = () => {
        // Only clear if this source is still the one we think is
        // active — avoids wiping a newer source on race conditions.
        if (voiceSource === src) {
          voiceSource = null;
          voiceGate = null;
        }
      };

      voiceGate = gate;
      voiceSource = src;
      src.start();
    });
  }

  function playSfx(t: SfxTrigger): void {
    if (!ctx || !sfxBus || !started) return;
    const url = `${SFX_DIR}/${t.name}.mp3${CACHE_BUST}`;
    const callGain = t.gain ?? 1.0;
    void loadBuffer(url).then((buf) => {
      if (!buf || !ctx || !sfxBus) return;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      if (callGain === 1.0) {
        src.connect(sfxBus);
      } else {
        // Per-call gain node keeps each SFX independently mixed so
        // subtle cues stay under the voice while big hits land full.
        const g = ctx.createGain();
        g.gain.value = callGain;
        src.connect(g);
        g.connect(sfxBus);
      }
      src.start();
    });
  }

  function fadeMusicTo(gain: number, durationSec: number): void {
    if (!ctx || !musicBus) return;
    const now = ctx.currentTime;
    musicBus.gain.cancelScheduledValues(now);
    musicBus.gain.setValueAtTime(musicBus.gain.value, now);
    musicBus.gain.linearRampToValueAtTime(gain, now + Math.max(0.05, durationSec));
  }

  function fadeVoiceTo(multiplier: number, durationSec: number): void {
    if (!ctx || !voiceDryBus || !voiceWetBus) return;
    const now = ctx.currentTime;
    const m = Math.max(0, multiplier);
    const ramp = Math.max(0.05, durationSec);
    for (const [bus, base] of [
      [voiceDryBus, VOICE_DRY_GAIN] as const,
      [voiceWetBus, VOICE_WET_GAIN] as const,
    ]) {
      bus.gain.cancelScheduledValues(now);
      bus.gain.setValueAtTime(bus.gain.value, now);
      bus.gain.linearRampToValueAtTime(base * m, now + ramp);
    }
  }

  function ensureHumsBus(): boolean {
    if (!ctx || !masterBus) return false;
    if (humsBus && humsFilter) return true;
    humsBus = ctx.createGain();
    humsBus.gain.value = HUMS_BUS_GAIN;
    humsFilter = ctx.createBiquadFilter();
    humsFilter.type = "lowpass";
    humsFilter.frequency.value = HUMS_FILTER_HZ;
    humsFilter.Q.value = HUMS_FILTER_Q;
    humsBus.connect(humsFilter);
    humsFilter.connect(masterBus);
    return true;
  }

  /** Allocate a gentle 2-sine hum voice: fundamental + subtle chorus
   *  partner. No sub-octave, no harmonics, no resonance — just a soft
   *  warm low-mid tone that settles in without attacking the ears. */
  function allocHumVoice(freq: number): HumVoice | null {
    if (!ctx || !humsBus) return null;
    const voiceGain = ctx.createGain();
    voiceGain.gain.value = 0;
    voiceGain.connect(humsBus);

    // Fundamental — the tonal anchor.
    const oscA = ctx.createOscillator();
    oscA.type = "sine";
    oscA.frequency.value = freq;
    oscA.connect(voiceGain);

    // Binaural partner at fundamental + BINAURAL_BEAT_HZ. Absolute
    // Hz offset (not cents) so the perceived beat frequency stays at
    // BINAURAL_BEAT_HZ regardless of what pitch oscA is playing. At
    // 0.35 gain the two oscillators don't read as two separate voices
    // — they fuse into one tone with a gentle amplitude modulation
    // that the brain hears as a sub-audible rhythm.
    const oscB = ctx.createOscillator();
    oscB.type = "sine";
    oscB.frequency.value = freq + BINAURAL_BEAT_HZ;
    const bGain = ctx.createGain();
    bGain.gain.value = 0.35;
    oscB.connect(bGain);
    bGain.connect(voiceGain);

    oscA.start();
    oscB.start();
    return {
      oscA,
      oscB,
      bGain,
      voiceGain,
      lastSeen: humTurn,
      curFreq: freq,
      curGain: 0,
    };
  }

  function setHumSources(sources: ReadonlyArray<HumSource>): void {
    if (!ctx || !started || muted) return;
    if (!ensureHumsBus()) return;
    const now = ctx.currentTime;
    humTurn++;

    // Cap the active voice count — keep only the loudest N so the
    // stack never muddies into destructive interference. Sort a
    // shallow copy so we don't mutate caller data.
    const topSources = [...sources]
      .filter((s) => s.gain >= 0.005)
      .sort((a, b) => b.gain - a.gain)
      .slice(0, HUM_MAX_VOICES);
    const kept = new Set(topSources.map((s) => s.id));

    for (const src of topSources) {
      let voice = humVoices.get(src.id);
      if (!voice) {
        const v = allocHumVoice(src.freq);
        if (!v) continue;
        voice = v;
        humVoices.set(src.id, voice);
      }
      voice.lastSeen = humTurn;

      // Ramp frequency if target moved meaningfully (>1 Hz). oscA
      // chases the target; oscB chases target + BINAURAL_BEAT_HZ so
      // the binaural beat stays locked at its design frequency across
      // every pitch change.
      if (Math.abs(src.freq - voice.curFreq) > 1) {
        voice.oscA.frequency.cancelScheduledValues(now);
        voice.oscA.frequency.setValueAtTime(voice.oscA.frequency.value, now);
        voice.oscA.frequency.linearRampToValueAtTime(src.freq, now + HUM_RAMP_SEC);

        voice.oscB.frequency.cancelScheduledValues(now);
        voice.oscB.frequency.setValueAtTime(voice.oscB.frequency.value, now);
        voice.oscB.frequency.linearRampToValueAtTime(
          src.freq + BINAURAL_BEAT_HZ,
          now + HUM_RAMP_SEC
        );

        voice.curFreq = src.freq;
      }

      // Ramp voice gain if target moved meaningfully.
      const targetGain = src.gain * HUM_VOICE_PEAK;
      if (Math.abs(targetGain - voice.curGain) > 0.005) {
        const g = voice.voiceGain.gain;
        g.cancelScheduledValues(now);
        g.setValueAtTime(g.value, now);
        g.linearRampToValueAtTime(targetGain, now + HUM_RAMP_SEC);
        voice.curGain = targetGain;
      }
    }

    // Release voices not in the current top-N. Smooth fade, then
    // stop all three oscillators and drop the voice.
    for (const [id, voice] of humVoices) {
      if (kept.has(id)) continue;
      const g = voice.voiceGain.gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(0, now + HUM_RELEASE_SEC);
      try {
        voice.oscA.stop(now + HUM_RELEASE_SEC + 0.02);
        voice.oscB.stop(now + HUM_RELEASE_SEC + 0.02);
      } catch {
        // Already stopped — ignore.
      }
      humVoices.delete(id);
      voice.curGain = 0;
    }
  }

  // ── Pluck synth (per-beat dopamine twinkles) ──────────────────
  // Glass-bell twinkle notes with a tempo-synced echo trail. Each
  // pluck is a stack of 4 harmonic sines (1x, 2x, 3x, 4x) with
  // decreasing amplitudes and shorter upper-harmonic decays — the
  // classic bell timbre. Routed through a delay network with
  // feedback so every note rings out in space, and into the voice
  // reverb for shared room tone.
  let plucksBus: GainNode | null = null;
  let pluckDelay: DelayNode | null = null;
  let pluckFeedback: GainNode | null = null;

  function ensurePlucksBus(): boolean {
    if (!ctx || !masterBus || !voiceWetBus) return false;
    if (plucksBus) return true;
    plucksBus = ctx.createGain();
    // Lower than it looks — human ears are most sensitive around
    // 2–4 kHz (Fletcher-Munson), so high twinkles at the same gain
    // read much louder than bass plucks. Keep this quiet.
    plucksBus.gain.value = 0.08;
    plucksBus.connect(masterBus);

    // Echo — stereo-ish dotted-eighth delay with gentle feedback so
    // each pluck gets 2–3 fading repeats. The DelayNode +
    // FeedbackGain loop is the standard ping-echo trick.
    pluckDelay = ctx.createDelay(1.5);
    pluckDelay.delayTime.value = 0.21;
    pluckFeedback = ctx.createGain();
    pluckFeedback.gain.value = 0.4;
    const delayWet = ctx.createGain();
    delayWet.gain.value = 0.55;
    // plucks → delay → wet → master
    plucksBus.connect(pluckDelay);
    pluckDelay.connect(pluckFeedback);
    pluckFeedback.connect(pluckDelay);
    pluckDelay.connect(delayWet);
    delayWet.connect(masterBus);

    // Also send a touch through the voice reverb convolver — shared
    // room tone between plucks and narration.
    const reverbSend = ctx.createGain();
    reverbSend.gain.value = 0.35;
    plucksBus.connect(reverbSend);
    reverbSend.connect(voiceWetBus);

    return true;
  }

  // Cached white-noise buffer reused for every pluck — no point
  // regenerating seconds of random samples per trigger.
  let pluckNoiseBuf: AudioBuffer | null = null;
  function getPluckNoise(): AudioBuffer | null {
    if (!ctx) return null;
    if (pluckNoiseBuf) return pluckNoiseBuf;
    const len = Math.floor(ctx.sampleRate * 1.2);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.55;
    }
    pluckNoiseBuf = buf;
    return buf;
  }

  function triggerPluck(opts: {
    freqHz: number;
    velocity?: number;
    decaySec?: number;
  }): void {
    if (!ctx || !started || muted) return;
    const noise = getPluckNoise();
    if (!noise || !ensurePlucksBus() || !plucksBus) return;
    const velocity = Math.max(0, Math.min(1, opts.velocity ?? 0.55));
    const decay = Math.max(0.15, opts.decaySec ?? 0.65);
    const now = ctx.currentTime;

    // Atonal shimmer: bandpass-filtered white noise. The filter
    // gives it a spectral "color" (so we can aim it high for a
    // twinkle) without any definite pitch — so every sparkle works
    // against ANY key the music happens to be in. This is the
    // sparkle of a wind chime / bell tree / shaker / celeste with
    // the pitch smeared out.
    const source = ctx.createBufferSource();
    source.buffer = noise;
    source.loop = false;

    // Bandpass centered at the requested "frequency" — high Q so
    // it reads as pitched shimmer, but it's still noise underneath
    // so it never locks onto a musical note.
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    // Small random drift per trigger so no two sparkles sound
    // identical — keeps them feeling like natural particles.
    const centerDrift = 1 + (Math.random() - 0.5) * 0.08;
    filter.frequency.setValueAtTime(opts.freqHz * centerDrift, now);
    filter.Q.value = 14;

    // Highpass safety net — prevent any low-frequency noise from
    // bleeding through (bandpass isn't perfectly sharp).
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 1800;
    hp.Q.value = 0.5;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(velocity * 0.5, now + 0.004);
    env.gain.exponentialRampToValueAtTime(0.001, now + decay);

    source.connect(filter);
    filter.connect(hp);
    hp.connect(env);
    env.connect(plucksBus);

    // Random starting offset into the noise buffer so consecutive
    // plucks aren't sampling identical waveform material.
    source.start(now, Math.random() * 0.5);
    source.stop(now + decay + 0.1);
  }

  function setMuted(value: boolean): void {
    muted = value;
    if (!masterBus || !ctx) return;
    masterBus.gain.cancelScheduledValues(ctx.currentTime);
    masterBus.gain.linearRampToValueAtTime(
      value ? 0 : 1,
      ctx.currentTime + 0.18
    );
  }

  function dispose(): void {
    try {
      musicSource?.stop();
    } catch {
      // already stopped
    }
    try {
      voiceSource?.stop();
    } catch {
      // already stopped
    }
    voiceSource = null;
    voiceGate = null;
    // Stop all hum voices cleanly.
    for (const voice of humVoices.values()) {
      try {
        voice.oscA.stop();
        voice.oscB.stop();
      } catch {
        // already stopped
      }
    }
    humVoices.clear();
    humsBus = null;
    humsFilter = null;
    void ctx?.close();
    ctx = null;
    started = false;
    buffers.clear();
    inFlight.clear();
  }

  return {
    ready: () => started,
    start,
    playVoice,
    playSfx,
    fadeMusicTo,
    fadeVoiceTo,
    setHumSources,
    triggerPluck,
    setMuted,
    muted: () => muted,
    dispose,
  };
}
